import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Check, Ruler, Palette, 
  ShoppingCart, CreditCard, RotateCcw, X, Layers,
  CheckCircle2, AlertCircle, Sparkles, Box, Cpu, Minus, Plus
} from 'lucide-react';
import { theme } from '../../utils/theme';
import { useLanguage } from '../../contexts/LanguageContext';
import { Slider } from '../ui/slider';
import { WINDOW_DIMENSIONS } from '../../utils/products';

export const ProductConfigurator = ({ onClose }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [step, setStep] = useState(1);
  const [unit, setUnit] = useState('cm'); // 'cm' or 'in'
  const [configType, setConfigType] = useState(null); // 'filter' | 'control'
  const [quantity, setQuantity] = useState(1);
  
  // Define constants inside component to access 't'
  const FILTER_STEPS = useMemo(() => [
    { id: 1, title: t.configurator?.steps?.type || 'Type', icon: Sparkles },
    { id: 2, title: t.configurator?.steps?.dimensions || 'Dimensions', icon: Ruler },
    { id: 3, title: t.configurator?.steps?.model || 'Modèle', icon: Layers },
    { id: 4, title: t.configurator?.steps?.design || 'Design', icon: Palette },
    { id: 5, title: t.configurator?.steps?.recap || 'Récapitulatif', icon: Check },
  ], [t]);

  const CONTROL_STEPS = useMemo(() => [
    { id: 1, title: t.configurator?.steps?.type || 'Type', icon: Sparkles },
    { id: 2, title: t.configurator?.steps?.control || 'Contrôle', icon: Cpu },
    { id: 3, title: t.configurator?.steps?.recap || 'Récapitulatif', icon: Check },
  ], [t]);

  const COLORS = useMemo(() => [
    { id: 'anthracite', name: t.configurator?.colors?.anthracite || 'Gris Anthracite', hex: '#374151', class: 'bg-gray-700' },
    { id: 'silver', name: t.configurator?.colors?.silver || 'Aluminium Brossé', hex: '#9CA3AF', class: 'bg-gray-400' },
    { id: 'black', name: t.configurator?.colors?.black || 'Noir Mat', hex: '#000000', class: 'bg-black' },
    { id: 'white', name: t.configurator?.colors?.white || 'Blanc Pur', hex: '#FFFFFF', class: 'bg-white' },
  ], [t]);

  const FILTER_MODELS = useMemo(() => [
    { 
      id: 'essential', 
      name: t.configurator?.models?.essential || 'Essentiel', 
      pricePerM2: 250, 
      description: t.configurator?.models?.essentialDesc || 'Protection efficace pour le quotidien.',
      features: ['Énergie solaire totale rejetée : 50%', 'Protection UV : >99%', 'Réflexion IR : 60%'],
      image: '/images/filters/VS61.png'
    },
    { 
      id: 'standard', 
      name: t.configurator?.models?.standard || 'Standard', 
      pricePerM2: 320, 
      description: t.configurator?.models?.standardDesc || 'Le parfait équilibre entre confort et protection.',
      features: ['Énergie solaire totale rejetée : 75%', 'Protection UV : >99%', 'Réflexion IR : 80%'],
      image: '/images/filters/VS30.png'
    },
    { 
      id: 'performance', 
      name: t.configurator?.models?.performance || 'Performance', 
      pricePerM2: 380, 
      description: t.configurator?.models?.performanceDesc || 'Isolation thermique et confort visuel optimal.',
      features: ['Énergie solaire totale rejetée : 84%', 'Protection UV : >99%', 'Réflexion IR : 82%'],
      image: '/images/filters/RN07.png'
    }
  ], [t]);

  const CONTROL_OPTIONS = useMemo(() => [
    {
      id: 'hub',
      name: t.configurator?.controls?.hub || 'Hub SAILEC',
      price: 120,
      description: t.configurator?.controls?.hubDesc || 'Le cerveau de votre installation.',
      features: ['Un seul Hub requis par pièce', 'Contrôle jusqu\'à 5 filtres'],
      image: 'https://images.unsplash.com/photo-1558002038-1091a166111c?auto=format&fit=crop&q=80&w=1000'
    },
    {
      id: 'none',
      name: t.configurator?.controls?.none || 'Aucun',
      price: 0,
      description: t.configurator?.controls?.noneDesc || 'Je possède déjà un Hub pour cette pièce.',
      features: ['J\'ai déjà un Hub', 'Économie de 120$'],
      image: 'https://images.unsplash.com/photo-1507146426996-ef05306b995a?auto=format&fit=crop&q=80&w=1000'
    }
  ], [t]);

  // Configuration State
  const [config, setConfig] = useState({
    width: [120],
    height: [180],
    depth: 3.0,
    selectedProduct: null,
    selectedControl: null,
    selectedColor: COLORS[0]
  });

  // Determine current steps based on config type
  const currentSteps = useMemo(() => {
    if (configType === 'control') return CONTROL_STEPS;
    return FILTER_STEPS;
  }, [configType, CONTROL_STEPS, FILTER_STEPS]);

  // Calculate Price based on dimensions (m2) and model price
  const calculatedPrice = useMemo(() => {
    if (configType === 'control') {
      return config.selectedControl ? config.selectedControl.price : 0;
    }
    if (configType === 'filter') {
      if (!config.selectedProduct) return 0;
      const areaInSqIn = (config.width[0] * config.height[0]) / 6.4516;
      return Math.round(100 + (areaInSqIn * 0.043));
    }
    return 0;
  }, [configType, config.width, config.height, config.selectedProduct, config.selectedControl]);

  const addToCart = () => {
    const timestamp = new Date().toISOString();
    const itemsToAdd = [];

    if (configType === 'filter') {
      // Add Filter Item
      itemsToAdd.push({
        id: Date.now(),
        type: 'filter',
        product: config.selectedProduct,
        dimensions: {
          width: config.width[0],
          height: config.height[0],
          depth: config.depth
        },
        color: config.selectedColor,
        price: calculatedPrice,
        quantity: quantity,
        timestamp
      });
    } else if (configType === 'control') {
      // Add Hub Item
      itemsToAdd.push({
        id: Date.now() + 1,
        type: 'hub',
        product: {
          name: config.selectedControl.name,
          image: config.selectedControl.image,
          description: config.selectedControl.description
        },
        price: calculatedPrice,
        quantity: quantity,
        timestamp
      });
    }

    const existingCart = JSON.parse(localStorage.getItem('sailec_cart') || '[]');
    localStorage.setItem('sailec_cart', JSON.stringify([...existingCart, ...itemsToAdd]));
    
    // Dispatch event for Navbar to update count
    window.dispatchEvent(new Event('storage'));
    
    // Move to success step (last step + 1)
    setStep(currentSteps.length + 1);
  };

  const handleNext = () => {
    if (step === 1 && !configType) return;
    
    if (configType === 'filter') {
      if (step === 3 && !config.selectedProduct) return; // Model step
    } else if (configType === 'control') {
      if (step === 2 && !config.selectedControl) return; // Control step
    }

    if (step < currentSteps.length + 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setConfigType(null); // Reset type selection when going back to start
    } else if (step > 1) {
      setStep(step - 1);
    }
  };

  const resetConfig = () => {
    setStep(1);
    setConfigType(null);
    setQuantity(1);
    setConfig({
      width: [120],
      height: [180],
      depth: 3.0,
      selectedProduct: null,
      selectedControl: null,
      selectedColor: COLORS[0]
    });
  };

  // Helper functions for unit conversion
  const toDisplay = (val) => unit === 'cm' ? val : Math.round(val / 2.54);
  const fromDisplay = (val) => unit === 'cm' ? val : Math.round(val * 2.54);
  
  const getSliderProps = (min, max, stepVal) => ({
    min: unit === 'cm' ? min : Math.round(min / 2.54),
    max: unit === 'cm' ? max : Math.round(max / 2.54),
    step: unit === 'cm' ? stepVal : 1
  });

  // --- RENDER STEPS ---

  const renderStepSelection = () => (
    <div className="animate-in fade-in slide-in-from-right-8 duration-700">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold mb-2 text-white">{t.configurator?.typeTitle || 'Que souhaitez-vous configurer ?'}</h3>
        <p className="text-gray-400">{t.configurator?.typeDesc || 'Sélectionnez le type de produit à ajouter.'}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Filter Option */}
        <div 
          onClick={() => { setConfigType('filter'); setStep(2); }}
          className="p-8 rounded-3xl border border-white/10 bg-[#0a0a0a] hover:bg-[#111] cursor-pointer transition-all duration-300 group flex flex-col items-center text-center gap-6"
          onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.colors.primary + '80'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
        >
          <div className="w-24 h-24 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500"
               style={{ backgroundColor: `${theme.colors.primary}1a`, color: theme.colors.primary }}>
            <Layers size={48} />
          </div>
          <div>
            <h4 className="text-2xl font-bold text-white mb-2">{t.configurator?.filterTitle || 'Filtre Intelligent'}</h4>
            <p className="text-gray-400">{t.configurator?.filterDesc || 'Protection solaire sur mesure pour vos fenêtres.'}</p>
          </div>
          <div className="mt-auto pt-4">
            <span className="font-bold flex items-center gap-2 group-hover:gap-4 transition-all" style={{ color: theme.colors.primary }}>
              {t.configurator?.configure || 'Configurer'} <ArrowRight size={20} />
            </span>
          </div>
        </div>

        {/* Control Option */}
        <div 
          onClick={() => { setConfigType('control'); setStep(2); }}
          className="p-8 rounded-3xl border border-white/10 bg-[#0a0a0a] hover:bg-[#111] cursor-pointer transition-all duration-300 group flex flex-col items-center text-center gap-6"
          style={{ '--hover-border-color': `${theme.colors.secondary}80` }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.colors.secondary + '80'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
        >
          <div className="w-24 h-24 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500"
               style={{ backgroundColor: `${theme.colors.secondary}1a`, color: theme.colors.secondary }}>
            <Cpu size={48} />
          </div>
          <div>
            <h4 className="text-2xl font-bold text-white mb-2">{t.configurator?.hubTitle || 'Hub de Contrôle'}</h4>
            <p className="text-gray-400">{t.configurator?.hubDesc || 'Le cerveau pour piloter vos filtres connectés.'}</p>
          </div>
          <div className="mt-auto pt-4">
            <span className="font-bold flex items-center gap-2 group-hover:gap-4 transition-all" style={{ color: theme.colors.secondary }}>
              {t.configurator?.configure || 'Configurer'} <ArrowRight size={20} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDimensions = () => (
    <div className="animate-in fade-in slide-in-from-right-8 duration-700">
      <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Visualizer */}
        <div className="bg-[#0a0a0a] rounded-3xl p-8 border border-white/5 flex items-center justify-center h-[400px] relative overflow-hidden group">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.03),_transparent_70%)]" />
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#333 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
            
            {/* The Window Preview */}
            <div 
                className="relative border-4 border-gray-700 bg-blue-900/10 backdrop-blur-sm transition-all duration-500 ease-out shadow-[0_0_50px_rgba(0,0,0,0.5)] flex items-center justify-center"
                style={{
                    width: `${Math.min(280, Math.max(100, config.width[0] * 1.2))}px`,
                    height: `${Math.min(280, Math.max(100, config.height[0] * 1.2))}px`,
                }}
            >
                {/* Glass Reflection */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent" />
                <div className="text-white/20 font-mono text-xs">SAILEC</div>
                
                {/* Dimensions Labels */}
                <div className="absolute -top-8 left-0 w-full text-center text-xs font-mono text-orange-500 font-bold">
                    {toDisplay(config.width[0])} {unit}
                </div>
                <div className="absolute -left-8 top-0 h-full flex items-center text-xs font-mono text-orange-500 font-bold -rotate-90">
                    {toDisplay(config.height[0])} {unit}
                </div>
            </div>
        </div>

        {/* Controls */}
        <div className="space-y-8">
          <div className="mb-2">
            <h3 className="text-3xl font-bold mb-2 text-white">{t.configurator?.dimensionsTitle || 'Dimensions'}</h3>
            <p className="text-gray-400">{t.configurator?.dimensionsDesc || 'Définissez la taille de votre protection solaire.'}</p>
          </div>

          {/* Unit Toggle */}
          <div className="flex justify-end">
            <div className="bg-[#111] p-1 rounded-lg border border-white/10 flex">
              <button 
                onClick={() => setUnit('cm')}
                className={`px-4 py-1 rounded-md text-xs font-bold transition-all ${unit === 'cm' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}
              >
                CM
              </button>
              <button 
                onClick={() => setUnit('in')}
                className={`px-4 py-1 rounded-md text-xs font-bold transition-all ${unit === 'in' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}
              >
                PO
              </button>
            </div>
          </div>

          <div>
            <label className="flex justify-between text-sm font-bold mb-4">
              <span className="text-gray-300">{t.configurator?.width || 'Largeur'}</span>
              <span style={{ color: theme.colors.primary }}>{toDisplay(config.width[0])} {unit}</span>
            </label>
            <Slider
              value={config.width.map(toDisplay)}
              onValueChange={(v) => setConfig({ ...config, width: v.map(fromDisplay), selectedProduct: null })}
              {...getSliderProps(WINDOW_DIMENSIONS.width.min, WINDOW_DIMENSIONS.width.max, WINDOW_DIMENSIONS.width.step)}
              className="w-full"
            />
          </div>

          <div>
            <label className="flex justify-between text-sm font-bold mb-4">
              <span className="text-gray-300">{t.configurator?.height || 'Hauteur'}</span>
              <span style={{ color: theme.colors.primary }}>{toDisplay(config.height[0])} {unit}</span>
            </label>
            <Slider
              value={config.height.map(toDisplay)}
              onValueChange={(v) => setConfig({ ...config, height: v.map(fromDisplay), selectedProduct: null })}
              {...getSliderProps(WINDOW_DIMENSIONS.height.min, WINDOW_DIMENSIONS.height.max, WINDOW_DIMENSIONS.height.step)}
              className="w-full"
            />
          </div>

          <div>
            <label className="flex justify-between text-sm font-bold mb-4">
              <span className="text-gray-300">{t.configurator?.depth || 'Profondeur du cadre'}</span>
              <span style={{ color: theme.colors.primary }}>{toDisplay(config.depth)} {unit}</span>
            </label>
            <select
              value={toDisplay(config.depth)}
              onChange={(e) => setConfig({ ...config, depth: fromDisplay(Number(e.target.value)), selectedProduct: null })}
              className="w-full px-4 py-3 rounded-xl border bg-[#111] text-white focus:outline-none focus:ring-2 transition-all"
              style={{ borderColor: theme.colors.border }}
            >
              {WINDOW_DIMENSIONS.depth.options.map(depth => (
                <option key={depth} value={toDisplay(depth)}>
                  {toDisplay(depth)} {unit}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderModel = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-700">
      <div className="text-center mb-6">
        <h3 className="text-3xl font-bold mb-2 text-white">{t.configurator?.modelTitle || 'Modèle'}</h3>
        <p className="text-gray-400">{t.configurator?.modelDesc || 'Choisissez la performance adaptée à vos besoins.'}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {FILTER_MODELS.map(product => (
          <div 
            key={product.id}
            onClick={() => setConfig({ ...config, selectedProduct: product })}
            className={`p-6 rounded-3xl border cursor-pointer transition-all duration-500 relative overflow-hidden group flex flex-col h-full ${
              config.selectedProduct?.id === product.id 
                ? 'border-orange-500 bg-[#151515] shadow-[0_0_30px_rgba(231,165,95,0.15)]' 
                : 'border-white/5 bg-[#0a0a0a] hover:border-white/20 hover:bg-[#111]'
            }`}
          >
            {/* Selection Indicator */}
            {config.selectedProduct?.id === product.id && (
              <div className="absolute top-4 right-4 text-orange-500 z-20">
                <CheckCircle2 size={20} />
              </div>
            )}

            {/* Image Area */}
            <div className="h-32 -mx-6 -mt-6 mb-6 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent z-10" />
              <img 
                src={product.image} 
                alt={product.name} 
                className="w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700" 
              />
            </div>

            <h4 className="text-xl font-bold text-white mb-2">{product.name}</h4>
            <p className="text-gray-400 text-sm mb-4 flex-grow">{product.description}</p>
            
            <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/5">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">{t.configurator?.exactPrice || 'Prix estimé'}</p>
              <p className="text-orange-500 font-bold text-lg">{Math.round(100 + (((config.width[0] * config.height[0]) / 6.4516) * 0.043))}$</p>
            </div>

            <ul className="space-y-2 mt-auto">
              {product.features.map((f, i) => (
                <li key={i} className="text-xs text-gray-300 flex items-start gap-2">
                  <Check size={12} className="mt-0.5 text-green-500 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  const renderControl = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-700">
      <div className="text-center mb-6">
        <h3 className="text-3xl font-bold mb-2 text-white">{t.configurator?.controlTitle || 'Contrôle'}</h3>
        <p className="text-gray-400">{t.configurator?.controlDesc || 'Sélectionnez votre Hub de contrôle.'}</p>
      </div>

      <div className="grid md:grid-cols-1 gap-6 max-w-md mx-auto">
        {CONTROL_OPTIONS.filter(opt => opt.id === 'hub').map(option => (
          <div 
            key={option.id}
            onClick={() => setConfig({ ...config, selectedControl: option })}
            className={`p-6 rounded-3xl border cursor-pointer transition-all duration-500 relative overflow-hidden group flex flex-col h-full ${
              config.selectedControl?.id === option.id 
                ? 'border-orange-500 bg-[#151515] shadow-[0_0_30px_rgba(231,165,95,0.15)]' 
                : 'border-white/5 bg-[#0a0a0a] hover:border-white/20 hover:bg-[#111]'
            }`}
          >
            {/* Selection Indicator */}
            {config.selectedControl?.id === option.id && (
              <div className="absolute top-4 right-4 text-orange-500 z-20">
                <CheckCircle2 size={20} />
              </div>
            )}

            {/* Image Area */}
            <div className="h-32 -mx-6 -mt-6 mb-6 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent z-10" />
              <img 
                src={option.image} 
                alt={option.name} 
                className="w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700" 
              />
            </div>

            <h4 className="text-xl font-bold text-white mb-2">{option.name}</h4>
            <p className="text-gray-400 text-sm mb-4 flex-grow">{option.description}</p>
            
            <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/5">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">{t.configurator?.price || 'Prix'}</p>
              <p className="text-orange-500 font-bold text-lg">{option.price === 0 ? (t.configurator?.included || 'Inclus') : `${option.price}$`}</p>
            </div>

            <ul className="space-y-2 mt-auto">
              {option.features.map((f, i) => (
                <li key={i} className="text-xs text-gray-300 flex items-start gap-2">
                  <Check size={12} className="mt-0.5 text-green-500 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDesign = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-700">
      <div className="text-center mb-6">
        <h3 className="text-3xl font-bold mb-2 text-white">{t.configurator?.designTitle || 'Design'}</h3>
        <p className="text-gray-400">{t.configurator?.designDesc || 'Une finition qui s\'intègre à votre intérieur.'}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-12 items-center">
        {/* Preview */}
        <div className="bg-[#0a0a0a] rounded-3xl p-8 border border-white/5 flex items-center justify-center h-[400px] relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.03),_transparent_70%)]" />
            
            {/* Window Frame */}
            <div 
                className="relative border-[12px] border-[#1e293b] shadow-2xl"
                style={{
                    width: '200px',
                    height: '300px',
                    backgroundColor: 'rgba(20, 30, 40, 0.6)'
                }}
            >
                {/* Cassette supérieure (Boîtier) */}
                <div 
                    className="absolute -top-[12px] -left-[12px] -right-[12px] h-[24px] transition-colors duration-500 z-20 shadow-md"
                    style={{ backgroundColor: config.selectedColor.hex }}
                >
                    <div className="absolute bottom-0 w-full h-[2px] bg-black/20" />
                </div>

                {/* Glass Effect */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent backdrop-blur-[1px]" />
                {/* Slats hint */}
                <div className="absolute inset-0 flex flex-col mt-2">
                    {[...Array(10)].map((_, i) => (
                        <div key={i} className="flex-1 border-b border-black/20" />
                    ))}
                </div>
            </div>
        </div>

        {/* Color Selection */}
        <div className="grid grid-cols-2 gap-4">
        {COLORS.map(color => (
          <div
            key={color.id}
            onClick={() => setConfig({ ...config, selectedColor: color })}
            className={`p-6 rounded-2xl border cursor-pointer transition-all duration-300 flex flex-col items-center gap-4 group ${
              config.selectedColor.id === color.id 
                ? 'border-orange-500 bg-[#151515]' 
                : 'border-white/5 bg-[#0a0a0a] hover:bg-[#111] hover:border-white/20'
            }`}
          >
            <div 
              className={`w-16 h-16 rounded-full shadow-lg border-4 transition-transform duration-300 ${config.selectedColor.id === color.id ? 'scale-110 border-white' : 'border-transparent group-hover:scale-105'}`}
              style={{ backgroundColor: color.hex }}
            >
                {config.selectedColor.id === color.id && (
                    <div className="w-full h-full flex items-center justify-center">
                        <Check size={24} className={color.id === 'white' ? 'text-black' : 'text-white'} />
                    </div>
                )}
            </div>
            <span className={`font-medium ${config.selectedColor.id === color.id ? 'text-white' : 'text-gray-400'}`}>
              {color.name}
            </span>
          </div>
        ))}
        </div>
      </div>
    </div>
  );

  const renderRecap = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-700">
      <div className="text-center mb-6">
        <h3 className="text-3xl font-bold mb-2 text-white">{t.configurator?.recapTitle || 'Récapitulatif'}</h3>
        <p className="text-gray-400">{t.configurator?.recapDesc || 'Vérifiez votre configuration.'}</p>
      </div>

      <div className="bg-[#0a0a0a] rounded-3xl border border-white/10 overflow-hidden max-w-lg mx-auto relative">
        {/* Decorative Top Bar */}
        <div className="h-2 w-full bg-gradient-to-r from-orange-500 to-yellow-500" />
        
        <div className="p-8 border-b border-white/5 flex gap-6 items-center">
          {configType === 'filter' ? (
            <>
              <div className="w-24 h-24 rounded-2xl bg-gray-800 overflow-hidden shrink-0 border border-white/10">
                <img src={config.selectedProduct?.image} alt="Product" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-xs text-orange-500 font-bold uppercase tracking-wider mb-1">{t.configurator?.selectedModel || 'Modèle sélectionné'}</div>
                <h4 className="text-2xl font-bold text-white mb-1">{config.selectedProduct?.name}</h4>
                <p className="text-gray-400 text-sm">{config.selectedColor.name}</p>
              </div>
            </>
          ) : (
            <>
              <div className="w-24 h-24 rounded-2xl bg-gray-800 overflow-hidden shrink-0 border border-white/10">
                <img src={config.selectedControl?.image} alt="Control" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-xs text-teal-500 font-bold uppercase tracking-wider mb-1">{t.configurator?.controlTitle || 'Contrôle'}</div>
                <h4 className="text-2xl font-bold text-white mb-1">{config.selectedControl?.name}</h4>
              </div>
            </>
          )}
        </div>
        
        <div className="p-8 space-y-6 bg-[#111]/50">
          {configType === 'filter' && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 flex items-center gap-2"><Ruler size={16} /> {t.configurator?.dimensionsTitle || 'Dimensions'}</span>
                <span className="text-white font-mono font-bold">{config.width[0]} x {config.height[0]} cm</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 flex items-center gap-2"><Box size={16} /> {t.configurator?.depthLabel || 'Profondeur'}</span>
                <span className="text-white font-mono font-bold">{config.depth} cm</span>
              </div>
            </>
          )}

          {configType === 'control' && (
             <p className="text-gray-400 text-sm">{config.selectedControl?.description}</p>
          )}

          <div className="h-px bg-white/5 my-4 border-t border-dashed border-gray-700" />

          <div className="flex justify-between items-center">
            <span className="text-gray-400">{t.configurator?.quantity || 'Quantité'}</span>
            <div className="flex items-center gap-3 bg-white/5 rounded-lg p-1 border border-white/10">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))} 
                className={`p-2 rounded-md transition-colors ${quantity <= 1 ? 'text-gray-600 cursor-not-allowed' : 'text-white hover:bg-white/10'}`}
                disabled={quantity <= 1}
              >
                <Minus size={16} />
              </button>
              <span className="font-bold w-8 text-center text-white">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="p-2 rounded-md text-white hover:bg-white/10 transition-colors">
                <Plus size={16} />
              </button>
            </div>
          </div>
          
          <div className="flex justify-between items-end">
            <span className="text-white">{t.configurator?.total || 'Total'}</span>
            <span className="text-4xl font-bold" style={{ color: theme.colors.primary }}>{calculatedPrice * quantity}$</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-12 animate-in zoom-in duration-700">
      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-900/20 flex items-center justify-center mx-auto mb-8 text-green-500 border border-green-500/30 shadow-[0_0_50px_rgba(34,197,94,0.2)]">
        <CheckCircle2 size={64} />
      </div>
      <h3 className="text-4xl font-bold text-white mb-4">{t.configurator?.successTitle || "C'est dans la boîte !"}</h3>
      <p className="text-gray-400 mb-12 max-w-md mx-auto text-lg">
        {t.configurator?.successDesc || "Votre configuration a été ajoutée au panier avec succès."}
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={resetConfig}
          className="px-8 py-4 rounded-full font-bold border border-white/10 hover:bg-white/5 text-white flex items-center justify-center gap-2 transition-all hover:scale-105"
        >
          <RotateCcw size={20} />
          {t.configurator?.configureAnother || "Configurer un autre"}
        </button>
        <button
          onClick={() => navigate('/cart')}
          className="px-8 py-4 rounded-full font-bold text-black flex items-center justify-center gap-2 transition-all hover:scale-105 shadow-[0_0_30px_rgba(231,165,95,0.4)]"
          style={{ background: theme.gradients.primary }}
        >
          <ShoppingCart size={20} />
          {t.configurator?.viewCart || "Voir le panier"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-[#050505]/95 backdrop-blur-xl overflow-y-auto">
      {/* Background Ambient Glow */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-orange-500/5 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="min-h-screen flex flex-col">
        
        {/* Header */}
        <div className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 px-4 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <button 
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
              <span className="font-bold text-lg text-white hidden sm:inline tracking-wide">{t.configurator?.headerTitle || 'Configurateur'} <span style={{color: theme.colors.primary}}>SAILEC</span></span>
            </div>
            
            {/* Progress Steps */}
            {step < currentSteps.length + 1 && (
              <div className="flex items-center justify-center gap-4">
                {currentSteps.map((s, i) => (
                  <div key={s.id} className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 border-2 ${
                      step >= s.id 
                        ? 'bg-orange-500 border-orange-500 text-black shadow-[0_0_15px_rgba(231,165,95,0.5)]' 
                        : 'bg-transparent border-gray-800 text-gray-600'
                    }`}>
                      {s.id}
                    </div>
                    {i < currentSteps.length - 1 && (
                      <div className={`w-12 h-0.5 mx-2 transition-colors duration-500 ${step > s.id ? 'bg-orange-500' : 'bg-gray-800'}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-grow flex items-center justify-center p-4 relative z-10">
          <div className="w-full max-w-6xl">
            {step === 1 && renderStepSelection()}
            
            {/* Filter Flow */}
            {configType === 'filter' && step === 2 && renderDimensions()}
            {configType === 'filter' && step === 3 && renderModel()}
            {configType === 'filter' && step === 4 && renderDesign()}
            {configType === 'filter' && step === 5 && renderRecap()}
            {configType === 'filter' && step === 6 && renderSuccess()}

            {/* Control Flow */}
            {configType === 'control' && step === 2 && renderControl()}
            {configType === 'control' && step === 3 && renderRecap()}
            {configType === 'control' && step === 4 && renderSuccess()}
          </div>
        </div>

        {/* Footer Actions (Hidden on Success step) */}
        {step < currentSteps.length + 1 && (
          <div className="sticky bottom-0 bg-[#050505]/80 backdrop-blur-xl border-t border-white/5 p-6 z-50">
            <div className="max-w-4xl mx-auto flex justify-between items-center">
              <button
                onClick={handleBack}
                disabled={false} // Always enabled to go back to selection
                className={`px-6 py-3 rounded-full font-bold flex items-center gap-2 transition-colors ${
                  step === 1 ? 'opacity-0 pointer-events-none' : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <ArrowLeft size={20} />
                {t.configurator?.back || 'Retour'}
              </button>

              <button
                onClick={step === currentSteps.length ? addToCart : handleNext}
                disabled={
                  (step === 1 && !configType) ||
                  (configType === 'filter' && step === 3 && !config.selectedProduct) ||
                  (configType === 'control' && step === 2 && !config.selectedControl)
                }
                className={`px-10 py-4 rounded-full font-bold text-black flex items-center gap-2 transition-all ${
                  (step === 1 && !configType) ||
                  (configType === 'filter' && step === 3 && !config.selectedProduct) ||
                  (configType === 'control' && step === 2 && !config.selectedControl)
                    ? 'opacity-50 cursor-not-allowed bg-gray-600'
                    : 'hover:scale-105 hover:shadow-[0_0_30px_rgba(231,165,95,0.3)]'
                }`}
                style={{ 
                  background: (step === 1 && !configType) ||
                  (configType === 'filter' && step === 3 && !config.selectedProduct) ||
                  (configType === 'control' && step === 2 && !config.selectedControl)
                    ? undefined 
                    : theme.gradients.primary 
                }}
              >
                {step === currentSteps.length ? (
                  <>
                    <ShoppingCart size={20} />
                    {t.configurator?.addToCart || 'Ajouter au panier'}
                  </>
                ) : (
                  <>
                    {t.configurator?.next || 'Suivant'}
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};